import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatError } from "@/api";
import { toast } from "sonner";
import { ArrowLeft, Landmark, ShieldCheck, Lock, Unlock, Eye, Play, IndianRupee, Loader2, Percent } from "lucide-react";

const STATUS_STYLES = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800" },
  verified: { label: "Verified", cls: "bg-emerald-100 text-emerald-800" },
  locked: { label: "Locked", cls: "bg-slate-900 text-white" },
};

function inr(n) { return `₹${Number(n || 0).toLocaleString("en-IN")}`; }

export default function SuperAdminPayoutsManagement() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [detailOrg, setDetailOrg] = useState(null);
  const [detailBank, setDetailBank] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [commissionForm, setCommissionForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: l }] = await Promise.all([
        api.get("/superadmin/payouts-management"),
        api.get("/superadmin/payouts?limit=100"),
      ]);
      setRows(m.orgs || []);
      setLedger(l.payouts || []);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (org) => {
    try {
      const { data } = await api.get(`/superadmin/orgs/${org.org_id}/bank`);
      setDetailBank(data);
      setDetailOrg(org);
      setEditing(false);
      setEditForm({
        account_holder: data.account_holder || "",
        bank_name: data.bank_name || "",
        account_number: "",
        ifsc: data.ifsc || "",
        default_consultation_fee: data.default_consultation_fee ?? "",
      });
      setCommissionForm({
        platform_commission_pct: org.platform_commission_pct ?? 0,
        gst_on_commission: org.gst_on_commission,
      });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const doAction = async (org, action) => {
    try {
      await api.post(`/superadmin/orgs/${org.org_id}/bank/${action}`, {});
      toast.success(`Bank ${action}ed`);
      if (detailOrg?.org_id === org.org_id) await openDetail(org);
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      const payload = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v === "" || v === undefined || v === null) return;
        payload[k] = k === "default_consultation_fee" ? Number(v) : v;
      });
      await api.put(`/superadmin/orgs/${detailOrg.org_id}/bank`, payload);
      toast.success("Bank details updated");
      setEditing(false);
      await openDetail(detailOrg);
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const saveCommission = async () => {
    try {
      await api.put(`/superadmin/orgs/${detailOrg.org_id}/commission`, {
        platform_commission_pct: Number(commissionForm.platform_commission_pct || 0),
        gst_on_commission: !!commissionForm.gst_on_commission,
      });
      toast.success("Commission updated");
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/superadmin/payouts/run-now");
      toast.success(`Payouts run: ${data.queued} queued of ${data.eligible} eligible`);
      await load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="superadmin-payouts-page">
      <header className="bg-white border-b border-slate-200">
        <div className="px-6 py-3 flex items-center justify-between max-w-[1400px] mx-auto gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/superadmin/dashboard")} data-testid="sa-payouts-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-lg font-semibold text-slate-900">Payouts &amp; Commission Management</h1>
              <p className="text-xs text-slate-500">Daily dispatch runs @ 8:00 PM IST</p>
            </div>
          </div>
          <Button onClick={runNow} disabled={running} data-testid="sa-run-payouts-now" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Run Payouts Now
          </Button>
        </div>
      </header>

      <main className="px-6 py-8 max-w-[1400px] mx-auto space-y-6">
        <Card className="medical-card p-0 overflow-hidden" data-testid="sa-orgs-table">
          <div className="p-5 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Clinics &amp; Hospitals ({rows.length})</h2>
            <p className="text-sm text-slate-500">Verify, edit, lock, and configure per-hospital commission.</p>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Hospital</TableHead>
                  <TableHead>Bank</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">MTD Net</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const s = STATUS_STYLES[r.bank_status] || STATUS_STYLES.pending;
                  return (
                    <TableRow key={r.org_id} data-testid={`sa-org-row-${r.org_id}`}>
                      <TableCell>
                        <div className="font-medium text-slate-900">{r.org_name}</div>
                        <div className="text-xs text-slate-500">{r.city || "—"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-700">{r.bank_holder || "—"}</div>
                        <div className="text-xs text-slate-500">{r.bank_name} · {r.account_number_masked || "—"}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${s.cls} text-xs`}>{s.label}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm font-medium text-slate-900">{r.platform_commission_pct}%</div>
                        <div className="text-[10px] text-slate-500">{r.gst_on_commission ? "+18% GST" : "no GST"}</div>
                      </TableCell>
                      <TableCell className="text-right font-medium text-slate-900">
                        {inr(r.mtd_totals?.net)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => openDetail(r)}
                            data-testid={`sa-view-${r.org_id}`} className="gap-1 h-8">
                            <Eye className="w-3.5 h-3.5" /> View
                          </Button>
                          {r.bank_status === "pending" && (
                            <Button size="sm" onClick={() => doAction(r, "verify")}
                              data-testid={`sa-verify-${r.org_id}`}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 h-8">
                              <ShieldCheck className="w-3.5 h-3.5" /> Verify
                            </Button>
                          )}
                          {r.bank_status === "verified" && (
                            <Button size="sm" onClick={() => doAction(r, "lock")}
                              data-testid={`sa-lock-${r.org_id}`}
                              className="bg-slate-900 hover:bg-slate-800 text-white gap-1 h-8">
                              <Lock className="w-3.5 h-3.5" /> Lock
                            </Button>
                          )}
                          {r.bank_status === "locked" && (
                            <Button size="sm" variant="outline" onClick={() => doAction(r, "unlock")}
                              data-testid={`sa-unlock-${r.org_id}`} className="gap-1 h-8">
                              <Unlock className="w-3.5 h-3.5" /> Unlock
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Ledger */}
        <Card className="medical-card p-0 overflow-hidden" data-testid="sa-payouts-ledger">
          <div className="p-5 border-b border-slate-200">
            <h2 className="font-heading text-lg font-semibold text-slate-900">Payout Ledger (last 100)</h2>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Hospital</TableHead>
                  <TableHead className="text-right">Gross</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Net Payout</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No payouts yet.</TableCell></TableRow>
                ) : ledger.map((p) => (
                  <TableRow key={p.id} data-testid={`sa-ledger-row-${p.id}`}>
                    <TableCell className="text-sm">{p.date}</TableCell>
                    <TableCell className="text-sm">{p.org_name}</TableCell>
                    <TableCell className="text-right">{inr(p.gross)}</TableCell>
                    <TableCell className="text-right text-rose-600">−{inr(p.commission)}</TableCell>
                    <TableCell className="text-right text-rose-600">−{inr(p.gst)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">{inr(p.net)}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{p.bank_txn_ref}</TableCell>
                    <TableCell><Badge className={p.status === "dispatched" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{p.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </main>

      {/* Detail dialog */}
      <Dialog open={!!detailOrg} onOpenChange={(v) => !v && (setDetailOrg(null), setDetailBank(null))}>
        <DialogContent className="max-w-2xl" data-testid="sa-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailOrg?.org_name} — Bank &amp; Commission</DialogTitle>
          </DialogHeader>
          {detailBank && (
            <div className="space-y-5">
              {/* Bank fields */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-slate-900">Bank Details</h4>
                  {!editing ? (
                    <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="sa-edit-toggle">Edit Fields</Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                      <Button size="sm" onClick={saveEdit} data-testid="sa-edit-save" className="bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
                    </div>
                  )}
                </div>
                {!editing ? (
                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <Kv k="Holder" v={detailBank.account_holder} />
                    <Kv k="Bank" v={detailBank.bank_name} />
                    <Kv k="Account #" v={detailBank.account_number || detailBank.account_number_masked} mono />
                    <Kv k="IFSC" v={detailBank.ifsc} mono />
                    <Kv k="Default Fee" v={detailBank.default_consultation_fee ? `₹${detailBank.default_consultation_fee}` : "—"} />
                    <Kv k="Status" v={detailBank.verification_status} />
                  </dl>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Holder" value={editForm.account_holder} onChange={(e) => setEditForm({...editForm, account_holder: e.target.value})} data-testid="sa-edit-holder" />
                    <Input placeholder="Bank" value={editForm.bank_name} onChange={(e) => setEditForm({...editForm, bank_name: e.target.value})} data-testid="sa-edit-bank" />
                    <Input placeholder="Account (blank keeps current)" value={editForm.account_number} onChange={(e) => setEditForm({...editForm, account_number: e.target.value.replace(/\s+/g,"")})} data-testid="sa-edit-acct" />
                    <Input placeholder="IFSC" maxLength={11} value={editForm.ifsc} onChange={(e) => setEditForm({...editForm, ifsc: e.target.value.toUpperCase()})} data-testid="sa-edit-ifsc" />
                    <Input type="number" placeholder="Default fee" value={editForm.default_consultation_fee} onChange={(e) => setEditForm({...editForm, default_consultation_fee: e.target.value})} data-testid="sa-edit-fee" />
                  </div>
                )}
              </div>

              {/* Commission */}
              <div className="pt-4 border-t border-slate-200">
                <h4 className="font-medium text-slate-900 mb-3">Commission &amp; GST Configuration</h4>
                <div className="grid grid-cols-2 gap-4 items-end">
                  <div>
                    <label className="text-xs text-slate-600 mb-1.5 block">Platform Commission %</label>
                    <div className="relative">
                      <Input type="number" min="0" max="100" step="0.5" className="pr-8"
                        value={commissionForm.platform_commission_pct}
                        onChange={(e) => setCommissionForm({...commissionForm, platform_commission_pct: e.target.value})}
                        data-testid="sa-commission-pct" />
                      <Percent className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-slate-900">18% GST on Commission</p>
                      <p className="text-[11px] text-slate-500">Auto-added on top of commission</p>
                    </div>
                    <Switch checked={!!commissionForm.gst_on_commission}
                      onCheckedChange={(v) => setCommissionForm({...commissionForm, gst_on_commission: v})}
                      data-testid="sa-gst-toggle" />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button size="sm" onClick={saveCommission} data-testid="sa-commission-save"
                    className="bg-slate-900 hover:bg-slate-800 text-white">Save commission</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ k, v, mono }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{k}</p>
      <p className={`text-sm text-slate-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{v || "—"}</p>
    </div>
  );
}
